/* eslint-disable indent */
/* eslint-disable no-tabs */
import React from 'react';
import {
  Box, ButtonBase, Typography, useTheme,
} from '@mui/material';
import { yellow } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import { FALLBACK_IMAGE_URL } from '@optimus/utils/src/constant';
import { useTranslation } from 'react-i18next';
import { TestIds } from '../../../assets/testIds';

interface ContentCardArticleProps {
  banner?: {
    url: string | undefined;
    width: number | undefined;
    height: number | undefined;
  } | null,
  OnclickListener?: () => void,
  locked?: boolean,
  // Owned by ContentCard so the whole card and this button share one handler.
  onUpgradeClick?: (e: React.MouseEvent) => void,
  /**
   * Advanced and Alliance are not sold in app, so content requiring either asks the
   * member to contact sales instead of offering an upgrade, per the Content chart on
   * product's "SN Logic on the Edge platform" page (SN-996). ContentCard decides this
   * and owns the request, so the badge only has to render the right label.
   */
  requiresSalesContact?: boolean,
  // Once the lead is away the badge acknowledges it rather than inviting a repeat request.
  hasContactedSales?: boolean,
}

export default function ContentCardArticle(props: ContentCardArticleProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  /** Shared by the call-to-action button and the post-request confirmation badge. */
  const lockedBadgeStyles = {
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
    backgroundColor: yellow[600],
    borderRadius: theme.spacing(0.75),
  } as const;

  const renderLockedAction = () => {
    if (props.requiresSalesContact && props.hasContactedSales) {
      return (
        <Box
          data-testid={TestIds.contentFeed.contactSalesConfirmationMsg}
          sx={lockedBadgeStyles}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: theme.spacing(1.5),
            }}
          >
            {t('content.contactSales.confirmation')}
          </Typography>
        </Box>
      );
    }

    const action = props.requiresSalesContact ?
      {
        labelKey: 'content.contactSales.cta',
        fallbackLabel: 'Contact Sales',
        testId: TestIds.contentFeed.contactSalesBtn,
      } :
      {
        labelKey: 'content.upgradeToUnlock',
        fallbackLabel: 'Upgrade to Unlock',
        testId: TestIds.contentFeed.upgradeBtn,
      };

    return (
      <ButtonBase
        aria-label={t(action.labelKey) || action.fallbackLabel}
        data-testid={action.testId}
        onClick={props.onUpgradeClick}
        sx={{
          ...lockedBadgeStyles,
          cursor: 'pointer',
          border: 'none',
          font: 'inherit',
          '&:hover': {
            backgroundColor: yellow[700],
          },
        }}
        type="button"
      >
        <LockIcon
          sx={{
            fontSize: theme.spacing(2),
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: theme.spacing(1.5),
            whiteSpace: 'nowrap',
          }}
        >
          {t(action.labelKey)}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
      <Box
        component="img"
        src={props?.banner?.url as string ?? FALLBACK_IMAGE_URL}
        alt="img"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: theme.spacing(2),
          border: '1px solid #DDD',
          ...(props.locked && {
            filter: 'blur(4px)',
          }),
        }}
      />
      {props.locked && (
        <Box
          data-testid={TestIds.contentFeed.lockedOverlay}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: alpha(yellow[800], 0.4),
            borderRadius: theme.spacing(2),
          }}
        >
          {renderLockedAction()}
        </Box>
      )}
    </Box>
  );
}
